/**
 * Texto do resultado, do resumo para prontuário e da folha de impressão.
 *
 * Fica tudo aqui por um motivo prático: antes, a interpretação exibida na tela
 * e o texto copiado eram construídos em lugares diferentes, e o copiado saía
 * com as chaves internas ("years1To4", "brucki") em vez dos rótulos. Com uma
 * fonte só, os três caminhos não têm como divergir.
 */
import {
  BRUCKI_2003_MEDIANS,
  LOURENCO_VERAS_2006,
  EDUCATION_OPTIONS,
  REFERENCE_OPTIONS
} from './calculator';
import { MEEM_BLOCKS } from './meemItems';

const num = (v) => String(v).replace('.', ',');

/** Data e hora locais no formato DD/MM/AAAA HH:MM (automática no texto do prontuário). */
export function formatarDataHora(data = new Date()) {
  const dois = (n) => String(n).padStart(2, '0');
  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} ${dois(data.getHours())}:${dois(data.getMinutes())}`;
}

/** Data no formato DD/MM/AA. */
export function formatarDataCurta(data = new Date()) {
  const dois = (n) => String(n).padStart(2, '0');
  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${dois(data.getFullYear() % 100)}`;
}

export function rotuloEscolaridade(education) {
  return EDUCATION_OPTIONS.find((o) => o.value === education)?.label || 'Não informada';
}

export function exemploEscolaridade(education) {
  return EDUCATION_OPTIONS.find((o) => o.value === education)?.exemplo || '';
}

export function rotuloReferencia(reference) {
  return REFERENCE_OPTIONS.find((o) => o.value === reference)?.label || 'Não informada';
}

/**
 * Interpretação do total conforme a referência escolhida.
 * Retorna também a mediana do grupo quando ela existe, para a folha impressa
 * poder mostrar o número ao lado do texto.
 */
export function interpretarTotal({ total, education, reference, ageConfirm }) {
  if (reference === 'brucki') {
    const mediana = BRUCKI_2003_MEDIANS[education];
    if (mediana === undefined) {
      return {
        medianaGrupo: null,
        texto: 'Sem a escolaridade informada, o total não pode ser comparado à mediana de nenhum grupo.'
      };
    }

    const diferenca = total - mediana;
    const absoluto = Math.abs(diferenca);
    const unidade = absoluto < 2 ? 'ponto' : 'pontos';
    const posicao =
      diferenca > 0
        ? `${num(absoluto)} ${unidade} acima`
        : diferenca < 0
          ? `${num(absoluto)} ${unidade} abaixo`
          : 'no mesmo valor';

    return {
      medianaGrupo: mediana,
      texto:
        `Comparação descritiva: o total ficou ${posicao} da mediana de ${num(mediana)} ` +
        'observada no grupo de escolaridade correspondente. A mediana divide o grupo ao ' +
        'meio e não é limite diagnóstico.'
    };
  }

  if (reference === 'lourenco') {
    if (ageConfirm !== 'yes') {
      return {
        medianaGrupo: null,
        texto: 'Esta referência foi estudada em pessoas com mais de 65 anos, e essa faixa etária não foi confirmada.'
      };
    }

    const config =
      education === 'noFormalEducation'
        ? LOURENCO_VERAS_2006.noFormalEducation
        : LOURENCO_VERAS_2006.anyFormalEducation;

    return {
      medianaGrupo: null,
      texto:
        total >= config.upperSideMinimum
          ? `Total igual ou superior à divisão de rastreio de ${config.upperSideMinimum} pontos. ` +
            `Com sensibilidade de ${num(config.sensitivity)}%, isso não exclui comprometimento ` +
            'cognitivo, sobretudo diante de queixa consistente.'
          : `Total abaixo da divisão de rastreio de ${config.upperSideMinimum} pontos ` +
            `(sensibilidade ${num(config.sensitivity)}%, especificidade ${num(config.specificity)}%). ` +
            'Indica necessidade de interpretação clínica e investigação adicional.'
    };
  }

  return {
    medianaGrupo: null,
    texto:
      `Total de ${total} em 30 pontos, sem comparação com referência populacional. ` +
      'O valor descreve o desempenho obtido e não pode ser interpretado isoladamente.'
  };
}

/** Desempenho por bloco, já com rótulos legíveis. */
export function desempenhoPorBloco(blockScores) {
  return MEEM_BLOCKS.map((bloco) => ({
    id: bloco.id,
    label: bloco.label,
    obtido: blockScores?.[bloco.id] ?? 0,
    max: bloco.max,
    mediana: bloco.normativo.mediana
  }));
}

/**
 * Resumo executivo para colar no prontuário.
 *
 * Texto puro, em português, sem chave interna e sem marcação — prontuário
 * eletrônico costuma rejeitar formatação colada.
 */
export function montarResumoProntuario({
  total,
  education,
  reference,
  ageConfirm,
  blockScores,
  porBloco = true,
  data = new Date()
}) {
  const { texto } = interpretarTotal({ total, education, reference, ageConfirm });
  const exemplo = exemploEscolaridade(education);

  const linhas = [
    'MINI EXAME DO ESTADO MENTAL (MEEM)',
    `Aplicado em ${formatarDataCurta(data)}`,
    '',
    `Pontuação total: ${total}/30`,
    `Escolaridade: ${rotuloEscolaridade(education)}${
      exemplo
        ? ` (${exemplo
            .replace(/\.$/, '')
            .replace(/\s*\([^)]*\)\s*$/, '')
            .replace(/^([A-ZÀ-Ú])/, (c) => c.toLowerCase())})`
        : ''
    }`,
    `Referência de interpretação: ${rotuloReferencia(reference)}`,
    '',
    `Interpretação: ${texto}`
  ];

  if (porBloco && blockScores) {
    linhas.push('', 'Desempenho por bloco:');
    desempenhoPorBloco(blockScores).forEach((b) => {
      linhas.push(`- ${b.label}: ${b.obtido}/${b.max}`);
    });
  }

  linhas.push(
    '',
    'Instrumento de rastreio cognitivo. A pontuação isolada não estabelece diagnóstico.',
    'Operacionalização conforme Brucki SMD et al. Sugestões para o uso do Mini-Exame do',
    'Estado Mental no Brasil. Arq Neuropsiquiatr. 2003;61(3-B):777-781.',
    'doi:10.1590/S0004-282X2003000500014',
    'Gerado em minimental.com.br — cálculo feito no navegador, sem envio de dados.'
  );

  return linhas.join('\n');
}

/**
 * Glossário dos termos estatísticos que aparecem ao lado dos dados normativos.
 */
export const GLOSSARIO_NORMATIVO = [
  {
    termo: 'n = 185',
    definicao:
      'Subamostra usada por Brucki et al. (2003) para analisar o desempenho nos subitens do teste. A amostra principal do estudo tinha 433 indivíduos saudáveis; 185 não deve ser apresentado como tamanho total do estudo.'
  },
  {
    termo: 'Mediana',
    definicao:
      'O valor do meio. Metade das pessoas do grupo pontuou abaixo dele e metade acima. É uma referência descritiva e não equivale, por si só, a um ponto de corte diagnóstico.'
  },
  {
    termo: 'Média',
    definicao:
      'A soma das pontuações dividida pelo número de pessoas. Diferente da mediana, é mais sensível a valores extremos e deve ser lida junto com a dispersão da amostra.'
  },
  {
    termo: 'DP (desvio padrão)',
    definicao:
      'Medida de dispersão dos resultados em torno da média. Quanto maior o desvio padrão, maior a variabilidade observada naquele grupo; ele não funciona como limiar diagnóstico isolado.'
  }
];

/** Escolaridade abreviada para o texto de uma linha. */
const ESCOLARIDADE_CURTA = {
  noFormalEducation: 'sem escolaridade formal',
  years1To4: '1–4 anos',
  years5To8: '5–8 anos',
  years9To11: '9–11 anos',
  years12OrMore: '12+ anos',
  none: 'não informada'
};

/**
 * Texto curto para colar no prontuário (sem nome nem dado de paciente):
 *   MEEM 24/30 (Orient temp 5/5, Orient esp 4/5, Mem 3/3, ...). Escolaridade 5–8 anos; mediana do grupo 26,5. Rastreio; não diagnóstico.
 * `grupos` = [{ copy, hits, max }]. `referencia` = { label } vindo de referenceMark, ou null.
 * A referência é descrita como ela é (mediana do grupo / divisão de rastreio), nunca como "corte" diagnóstico.
 */
export function montarResumoCurto({ total, grupos, education, referencia, naoAvaliados = 0, emBloco = false, aplicadoEm = '' }) {
  const partes = grupos.map((g) => `${g.copy} ${g.hits}/${g.max}`).join(', ');
  const ref = referencia ? `; ${referencia.label.replace(': ', ' ')}` : '';
  const extras = [
    naoAvaliados > 0 ? `${naoAvaliados} ${naoAvaliados === 1 ? 'item não avaliado' : 'itens não avaliados'}.` : '',
    emBloco ? 'Marcação em bloco.' : ''
  ].filter(Boolean).join(' ');
  // Sem escolaridade informada, a parte "Escolaridade…; mediana…" é omitida (nada é inventado).
  const informada = education && education !== 'none' && ESCOLARIDADE_CURTA[education];
  const escolaridade = informada ? ` Escolaridade ${ESCOLARIDADE_CURTA[education]}${ref}.` : '';
  return `MEEM ${total}/30${partes ? ` (${partes})` : ''}.${escolaridade} Rastreio; não diagnóstico.${extras ? ` ${extras}` : ''}${aplicadoEm ? ` Aplicado em ${aplicadoEm}.` : ''}`;
}
